import SkinKey from "../../../core/ui/SkinKey";
import Vector2 from "./Vector2";

class Graph {
    /**
     * Create a graph
     * @param {SkinKey} skin 
     * @param {{ GraphAxesDisplayOptions }} displayOptions
     */
    constructor(skin, data, minimumBoundaries = new Vector2(x,y), displayOptions = {}) {
        this._skin = skin;
        this._displayOptions = displayOptions || {};


        this.setData(data);
        this.setBoundaries(data, minimumBoundaries);

        this.draw();
    }

    setXAxisStyle(visible, color, weight) {
        // bottom line + Grey + specific size
    }

    setData(data) {
        this._data = data;
    }
    getData() {
        return this._data;
    }

    setBoundaries(data, minimumBoundaries) {
        this._minData = new Vector2(
            Math.min(...data.map(a => a.x)),
            Math.min(...data.map(a => a.y))
        );
        this._maxData = new Vector2(
            Math.max(...data.map(a => a.x), minimumBoundaries.x),
            Math.max(...data.map(a => a.y), minimumBoundaries.y)
        );
        this._minPosition = {
            x: new Vector2(0, this._maxData.y),
            y: new Vector2(0, this._maxData.y),
        };
        this._maxPosition = {
            x: new Vector2(this._maxData.x, this._maxData.y),
            y: new Vector2(0, 0),
        };
        this._scaledMinPosition = {
            x: this._scalePoint(this._minPosition.x),
            y: this._scalePoint(this._minPosition.y),
        };
        this._scaledMaxPosition = {
            x: this._scalePoint(this._maxPosition.x),
            y: this._scalePoint(this._maxPosition.y),
        };
    }

    // Util
    _getPointAtPercent(src, dest, percent) {
        let x = src.x + (dest.x - src.x) * percent;
        let y = src.y + (dest.y - src.y) * percent;
        return new Vector2(x, y);
    }

    _offsetPoint(point, offset) {
        return new Vector2(point.x + offset.x, point.y + offset.y);
    }

    // Util
    _middleBetween2Vector2(v2A, v2B) {
        return new Vector2((v2A.x + v2B.x) / 2, (v2A.y + v2B.y) / 2);
    }

    draw() {
        this._drawAxes();
        this._drawIntervals();
        this._drawLegends();
        this._drawGraph();
    }
    _drawLine(shape, from, to) {
        shape.graphics.moveTo(from.x, from.y);
        shape.graphics.lineTo(to.x, to.y);
        this._skin.addChild(shape);
    }

    _drawAxes() {
        let { graphAxesDisplayOptions } = this._displayOptions;
        this._drawAxe(graphAxesDisplayOptions.xAxis, this._scaledMinPosition.x, this._scaledMaxPosition.x);
        this._drawAxe(graphAxesDisplayOptions.yAxis, this._scaledMinPosition.y, this._scaledMaxPosition.y);
    }

    _drawAxe(graphAxisDisplayOptions, from, to) {
        if (graphAxisDisplayOptions.visible) {
            let shape = new createjs.Shape();
            shape.snapToPixel = true;
            shape.graphics
                .setStrokeStyle(graphAxisDisplayOptions.thickness)
                .beginStroke(graphAxisDisplayOptions.color);
            this._drawLine(shape, from, to);
        }
    }


    _drawIntervals() {
        let { graphAxesDisplayOptions } = this._displayOptions;
        this._drawInterval(
            graphAxesDisplayOptions.xAxis.interval,
            this._maxData.x,
            this._scaledMinPosition.x,
            this._scaledMaxPosition.x,
            new Vector2(0, graphAxesDisplayOptions.xAxis.interval.offset),
            "center",
            "top",
            new Vector2(null, this._scaledMaxPosition.y.y),
        );


        this._drawInterval(
            graphAxesDisplayOptions.yAxis.interval,
            this._maxData.y,
            this._scaledMinPosition.y,
            this._scaledMaxPosition.y,
            new Vector2(-graphAxesDisplayOptions.xAxis.interval.offset, 0),
            "right",
            "middle",
            new Vector2(this._scaledMaxPosition.x.x, null)
        );
    }

    _drawInterval(intervalOptions, maxData, from, to, offset, horizontalAligment = "center", verticalAlignment = "middle", intervalLineMaxPosition = null) {
        if (intervalOptions.visible) {
            var interval = intervalOptions.interval;
            var index =  (intervalOptions.startsAt0 ? 0 : interval);
            while (index < maxData + interval) {
                let factor = index / maxData;
                let scaledPoint = this._getPointAtPercent(from, to, factor);
                let finalPoint = this._offsetPoint(scaledPoint, offset);

                let intervalText = new createjs.Text(index, intervalOptions.fontSize + " " + intervalOptions.font, intervalOptions.color);
                intervalText.x = finalPoint.x;
                intervalText.y = finalPoint.y;
                intervalText.textAlign = horizontalAligment;
                intervalText.textBaseline = verticalAlignment;
                this._skin.addChild(intervalText);

                if(intervalOptions.lines) {
                    let destinationLine = new Vector2(intervalLineMaxPosition.x, intervalLineMaxPosition.y);
                    let shape = new createjs.Shape();
                    shape.graphics
                        .setStrokeStyle(intervalOptions.linesTickness)
                        .beginStroke(intervalOptions.linesColor);

                    // Todo: Make it more generic and less specific to the current case.
                    if(intervalLineMaxPosition.x == 0) {
                        destinationLine.x = scaledPoint.x;
                    }
                    if(intervalLineMaxPosition.y == 0) {
                        destinationLine.y = scaledPoint.y;
                    }
                    this._drawLine(shape, scaledPoint, destinationLine);
                }
                
                index += interval;
            }
        }
    }
    _drawLegends() {
        let { graphAxesDisplayOptions } = this._displayOptions;
        this._drawLegend(
            graphAxesDisplayOptions.xAxis.legend,
            this._getPointAtPercent(this._scaledMinPosition.x, this._scaledMaxPosition.x, 0.5),
            new Vector2(0, graphAxesDisplayOptions.xAxis.legend.offset),
            "center",
            "top"

        );
        this._drawLegend(
            graphAxesDisplayOptions.yAxis.legend,
            this._getPointAtPercent(this._scaledMinPosition.y, this._scaledMaxPosition.y, 0.5),
            new Vector2(-graphAxesDisplayOptions.xAxis.legend.offset, 0),
            "right",
            "middle"
        );
    }

    _drawLegend(legendOptions, position, offset, horizontalAligment = "center", verticalAlignment = "middle") {
        position = this._offsetPoint(position, offset);
        if (legendOptions.visible) {
            let legend = new createjs.Text(legendOptions.text, legendOptions.fontSize + " " + legendOptions.font, legendOptions.color);
            legend.x = position.x;
            legend.y = position.y;
            legend.textAlign = horizontalAligment;
            legend.textBaseline = verticalAlignment;
            this._skin.addChild(legend);
        }
    }

    _processPositionPoint(point) {
        return this._scalePoint(new Vector2(point.x, this._maxData.y - point.y));
    }

    _scalePoint(point) {
        let { graphAxesDisplayOptions } = this._displayOptions;
        return new Vector2(point.x * graphAxesDisplayOptions.xAxis.scale, point.y * graphAxesDisplayOptions.yAxis.scale);
    }

    _drawDot(shape, position, radius = 1, fillColor = "black") {
        shape.graphics.beginFill(fillColor).drawCircle(position.x, position.y, radius);
        this._skin.addChild(shape);
    }

    _drawGraph() {
        let { lineGraphDisplayOptions } = this._displayOptions;
        let shape = new createjs.Shape();
        shape.snapToPixel = true;
        shape.graphics
            .setStrokeStyle(lineGraphDisplayOptions.thickness)
            .beginStroke(lineGraphDisplayOptions.color);

        var originPoint = null;
        for (var newPoint of this._data) {
            let newProcessedPoint = this._processPositionPoint(newPoint);
            this._drawDot(shape, newProcessedPoint, lineGraphDisplayOptions.dotThickness, lineGraphDisplayOptions.color);
            if (originPoint != null) {
                this._drawLine(shape, originPoint, newProcessedPoint);
            }
            originPoint = newProcessedPoint;
        }
        this._skin.addChild(shape);
    }
}
export default Graph;